'use client';

import { useTranslations } from 'next-intl';
import { ScanLine, FileText, Loader2, Eye } from 'lucide-react';
import { useRouter } from '@/i18n/routing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RouteGate } from '@/components/route-gate';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/use-permissions';
import { RepairHistoryButton } from './repair-history-button';
import type { BatchStatus, ScanBatch } from '../types';

interface PhotoScanningCompactRowProps {
  batch: ScanBatch;
  onStartScanning: (batch: ScanBatch) => void;
  onViewPdf: (batch: ScanBatch) => void;
  isScanningDisabled: (batch: ScanBatch) => boolean;
  getScanDisabledReason: (batch: ScanBatch) => string | null;
  getScanButtonLabel: (batch: ScanBatch) => string;
  isStartPending: boolean;
  isPdfLoading: boolean;
}

const STATUS_STYLES: Record<BatchStatus, string> = {
  pending: 'border-gray-500/30 bg-gray-500/10 text-gray-600 dark:text-gray-400',
  confirmed:
    'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400',
  in_progress:
    'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400',
  completed:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
};

const STATUS_LABEL_KEYS: Record<BatchStatus, string> = {
  pending: 'statusPending',
  confirmed: 'statusAssigned',
  in_progress: 'statusInProgress',
  completed: 'statusCompleted'
};

export function PhotoScanningCompactRow({
  batch,
  onStartScanning,
  onViewPdf,
  isScanningDisabled,
  getScanDisabledReason,
  getScanButtonLabel,
  isStartPending,
  isPdfLoading
}: PhotoScanningCompactRowProps) {
  const t = useTranslations('photoScanning');
  const router = useRouter();
  const { hasPermission } = usePermissions();
  const canSeeContainer = hasPermission(
    'photo_scanning.column.columnContainerId'
  );
  const canSeeStatus = hasPermission('photo_scanning.column.columnStatus');

  const disabled = isScanningDisabled(batch);
  const disabledReason = getScanDisabledReason(batch);

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      {/* Left: batch number + container */}
      <div className='min-w-0 shrink-0'>
        <span className='font-mono text-sm font-medium'>
          {batch.batch_number}
        </span>
        {canSeeContainer && (
          <span className='text-muted-foreground ms-2 font-mono text-xs'>
            {batch.container_id}
          </span>
        )}
      </div>

      {/* Center: progress + client name */}
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <span className='text-muted-foreground text-sm'>
            {batch.scanned_pages}/{batch.total_pages}
          </span>
          <span className='text-muted-foreground hidden truncate text-sm md:inline'>
            {batch.client_name}
          </span>
        </div>
      </div>

      {/* Right: status badge + actions */}
      {canSeeStatus && (
        <Badge
          variant='outline'
          className={cn(
            'shrink-0 text-xs uppercase',
            STATUS_STYLES[batch.status]
          )}
        >
          {t(STATUS_LABEL_KEYS[batch.status])}
        </Badge>
      )}

      <div className='flex shrink-0 items-center gap-1'>
        {batch.registration_id != null && (
          <RouteGate route={`/dashboard/containers/${batch.registration_id}`}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size='icon'
                  variant='ghost'
                  onClick={() =>
                    router.push(
                      `/dashboard/containers/${batch.registration_id}`
                    )
                  }
                  className='h-7 w-7'
                >
                  <Eye className='h-4 w-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('viewContainer')}</TooltipContent>
            </Tooltip>
          </RouteGate>
        )}
        {batch.status === 'completed' ? (
          <Button
            size='sm'
            variant='outline'
            onClick={() => onViewPdf(batch)}
            disabled={isPdfLoading}
            className='h-7 gap-1.5'
          >
            {isPdfLoading ? (
              <Loader2 className='h-3.5 w-3.5 animate-spin' />
            ) : (
              <FileText className='h-3.5 w-3.5' />
            )}
            <span className='hidden sm:inline'>{t('viewPdf')}</span>
          </Button>
        ) : disabled && disabledReason ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='inline-block'>
                <Button
                  size='sm'
                  disabled
                  className='pointer-events-none h-7 opacity-50'
                >
                  <ScanLine className='me-1.5 h-3.5 w-3.5' />
                  <span className='hidden sm:inline'>
                    {getScanButtonLabel(batch)}
                  </span>
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{disabledReason}</TooltipContent>
          </Tooltip>
        ) : (
          <Button
            size='sm'
            className='h-7'
            onClick={() => onStartScanning(batch)}
            disabled={isStartPending}
          >
            <ScanLine className='me-1.5 h-3.5 w-3.5' />
            <span className='hidden sm:inline'>
              {getScanButtonLabel(batch)}
            </span>
          </Button>
        )}
        {batch.repair_count > 0 && (
          <RepairHistoryButton batch={batch} label={t('viewRepairHistory')} />
        )}
      </div>
    </div>
  );
}
